import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article';
  image?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

const SITE_NAME = 'Nexihow';
const BASE_URL = 'https://nexihow.lovable.app';

const SEO = ({ 
  title, 
  description, 
  canonical, 
  type = 'website',
  image = `${BASE_URL}/og-image.png`,
  jsonLd,
  noindex = false
}: SEOProps) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const truncatedDescription = description.length > 160 ? description.substring(0, 157) + '...' : description;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Helper function to generate checklist JSON-LD (HowTo schema with enhanced data)
export const generateChecklistJsonLd = (checklist: {
  title: string;
  description: string;
  items: { text: string; note?: string }[];
  category: string;
  id: string;
  tags?: string[];
}) => {
  const estimatedTime = Math.ceil(checklist.items.length * 2);
  
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: checklist.title,
    description: checklist.description,
    image: `${BASE_URL}/og-image.png`,
    totalTime: `PT${estimatedTime}M`,
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '0',
    },
    supply: checklist.items.slice(0, 10).map(item => ({
      '@type': 'HowToSupply',
      name: item.text,
    })),
    tool: [],
    step: checklist.items.map((item, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: item.text,
      text: item.note ? `${item.text} - ${item.note}` : item.text,
      url: `${BASE_URL}/checklist/${checklist.id}#step-${index + 1}`,
    })),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/checklist/${checklist.id}`,
    },
    author: {
      '@type': 'Organization',
      name: 'Nexihow',
      url: BASE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Nexihow',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo.png`,
      },
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    keywords: checklist.tags?.join(', ') || checklist.category,
  };
};

// Generate ItemList schema for checklist items (for rich snippets)
export const generateChecklistItemListJsonLd = (checklist: {
  title: string;
  description: string;
  items: { text: string }[];
  id: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: checklist.title,
    description: checklist.description,
    url: `${BASE_URL}/checklist/${checklist.id}`,
    numberOfItems: checklist.items.length,
    itemListElement: checklist.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.text,
    })),
  };
};

// Generate Article schema for better search presence
export const generateArticleJsonLd = (article: {
  title: string;
  description: string;
  url: string;
  category?: string;
  tags?: string[];
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `${BASE_URL}${article.url}`,
    image: `${BASE_URL}/og-image.png`,
    author: {
      '@type': 'Organization',
      name: 'Nexihow',
      url: BASE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Nexihow',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo.png`,
      },
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}${article.url}`,
    },
    articleSection: article.category,
    keywords: article.tags?.join(', '),
  };
};

// Helper function for category page JSON-LD
export const generateCategoryJsonLd = (category: {
  name: string;
  description: string;
  slug: string;
  checklistCount: number;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} Checklists`,
    description: category.description,
    url: `${BASE_URL}/category/${category.slug}`,
    numberOfItems: category.checklistCount,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: category.checklistCount,
      name: `${category.name} Checklists Collection`,
    },
  };
};

// FAQ Schema for pages with questions
export const generateFAQJsonLd = (faqs: { question: string; answer: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Breadcrumb JSON-LD
export const generateBreadcrumbJsonLd = (items: { name: string; url: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  };
};

// Website JSON-LD for homepage
export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Nexihow',
  alternateName: 'Nexihow Checklists',
  description: 'AI-generated checklists for every situation in life. Find or generate the perfect checklist for moving, travel, work, and more.',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'en-US',
};

// Organization JSON-LD
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Nexihow',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  description: 'Nexihow provides AI-generated checklists for every situation in life.',
  sameAs: [],
};

// Function to get translated FAQs
export const getTranslatedFAQs = (t: (key: string) => string) => [
  {
    question: t('faq.whatIs.question'),
    answer: t('faq.whatIs.answer'),
  },
  {
    question: t('faq.forWhom.question'),
    answer: t('faq.forWhom.answer'),
  },
  {
    question: t('faq.experience.question'),
    answer: t('faq.experience.answer'),
  },
  {
    question: t('faq.difference.question'),
    answer: t('faq.difference.answer'),
  },
  {
    question: t('faq.teams.question'),
    answer: t('faq.teams.answer'),
  },
  {
    question: t('faq.privacy.question'),
    answer: t('faq.privacy.answer'),
  },
  {
    question: t('faq.export.question'),
    answer: t('faq.export.answer'),
  },
  {
    question: t('faq.free.question'),
    answer: t('faq.free.answer'),
  },
  {
    question: t('faq.unique.question'),
    answer: t('faq.unique.answer'),
  },
  {
    question: t('faq.solo.question'),
    answer: t('faq.solo.answer'),
  },
  {
    question: t('faq.install.question'),
    answer: t('faq.install.answer'),
  },
];

// Homepage FAQ for rich snippets (English for SEO)
export const homepageFAQs = [
  {
    question: 'What is Nexihow?',
    answer: 'Nexihow is an AI-powered tool that helps you structure tasks and processes into clear, actionable plans. Whether for personal organization or team workflows and SOPs, Nexihow turns natural language into clarity in seconds.',
  },
  {
    question: 'Is Nexihow for personal use or for businesses?',
    answer: 'Both. Individuals use Nexihow to organize daily tasks and life events. Teams and businesses use it to create workflows, SOPs, onboarding processes, and repeatable systems — using the same AI engine.',
  },
  {
    question: 'Do I need experience writing workflows or SOPs?',
    answer: 'No. You simply describe what you\'re trying to do in plain language. Nexihow automatically structures it into a clear workflow or SOP.',
  },
  {
    question: 'What\'s the difference between a checklist, workflow, and SOP?',
    answer: 'A checklist helps complete tasks. A workflow shows how steps relate and flow together. An SOP documents a repeatable process for consistent execution. Nexihow can generate all three from the same input.',
  },
  {
    question: 'Can Nexihow be used by teams?',
    answer: 'Yes. With the Team plan, you can collaborate in shared workspaces, manage workflows and SOPs, assign steps, and track progress together.',
  },
  {
    question: 'Is my data private?',
    answer: 'Yes. Your data is private and secure. Your content is not shared with other users.',
  },
  {
    question: 'Can I export workflows or SOPs?',
    answer: 'Yes. Premium and Team plans support exporting to formats like PDF and DOCX.',
  },
  {
    question: 'Is there a free plan?',
    answer: 'Yes. You can start for free and upgrade only when you need more advanced features.',
  },
  {
    question: 'What makes Nexihow different from other productivity tools?',
    answer: 'Most tools require you to build structure manually. Nexihow creates the structure for you using AI.',
  },
  {
    question: 'Can I use Nexihow without a team?',
    answer: 'Absolutely. Many users use Nexihow solo for personal organization or professional work.',
  },
  {
    question: 'Do I need to install anything?',
    answer: 'No. Nexihow runs entirely in your browser and works on desktop and mobile.',
  },
];
